/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function formatBytes(bytes: number, si = false, dp = 1): string {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
}

/**
 * Return the distance between the given date and now in words.
 *
 * @param date The date
 * @return Distance in words
 */
export function formatDistanceToNow(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'less than a minute';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'}`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'}`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears === 1 ? '' : 's'}`;
}

/**
 * Format date to readable string
 *
 * @param date The date
 * @param options Intl.DateTimeFormatOptions
 * @return Formatted date string
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };

  return d.toLocaleDateString(undefined, defaultOptions);
}

/**
 * Format date and time to readable string
 *
 * @param date The date
 * @param options Intl.DateTimeFormatOptions
 * @return Formatted datetime string
 */
export function formatDateTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };

  return d.toLocaleDateString(undefined, defaultOptions);
}

/**
 * Truncate text to specified length
 *
 * @param text The text to truncate
 * @param length Maximum length
 * @param suffix Suffix to append when truncated
 * @return Truncated text
 */
export function truncateText(text: string, length: number, suffix = '...'): string {
  if (text.length <= length) {
    return text;
  }

  return text.slice(0, length - suffix.length) + suffix;
}

/**
 * Highlight search terms in text
 *
 * @param text The text to highlight
 * @param searchTerms Array of terms to highlight
 * @param className CSS class name for highlights
 * @return HTML string with highlighted terms
 */
export function highlightSearchTerms(
  text: string,
  searchTerms: string[],
  className = 'search-highlight'
): string {
  if (!searchTerms.length) return text;

  let highlightedText = text;

  searchTerms.forEach(term => {
    if (term.trim()) {
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      highlightedText = highlightedText.replace(
        regex,
        `<mark class="${className}">$1</mark>`
      );
    }
  });

  return highlightedText;
}

/**
 * Convert camelCase or PascalCase to readable text
 *
 * @param text The text to convert
 * @return Readable text
 */
export function camelCaseToReadable(text: string): string {
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

/**
 * Format number with thousands separator
 *
 * @param num The number to format
 * @param locale The locale to use for formatting
 * @return Formatted number string
 */
export function formatNumber(num: number, locale?: string): string {
  return num.toLocaleString(locale);
}

/**
 * Format percentage
 *
 * @param value The value (0-1 or 0-100)
 * @param decimals Number of decimal places
 * @param asDecimal Whether the input value is a decimal (0-1) or percentage (0-100)
 * @return Formatted percentage string
 */
export function formatPercentage(
  value: number,
  decimals = 1,
  asDecimal = true
): string {
  const percentage = asDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
}
